<template>
  <div class="app-nav-root">
    <header class="app-nav" :class="{ 'nav-scrolled': isScrolled }">
      <div class="nav-container">
        <div class="nav-content">
          <router-link to="/" class="nav-brand" aria-label="Municipal Collectionz home">
            <BrandMark :size="38" />
            <span class="nav-wordmark">
              <span class="wm-top">{{ brand.top }}</span>
              <span class="wm-bottom">Collectionz</span>
            </span>
          </router-link>

          <nav class="nav-links" aria-label="Main">
            <router-link
              v-for="link in links"
              :key="link.to"
              :to="link.to"
              class="nav-link"
              :class="{ 'is-active': isActive(link) }"
            >
              {{ link.label }}
            </router-link>
          </nav>

          <div class="nav-actions">
            <router-link to="/login" class="nav-login">
              <SiteIcon name="login" :size="17" />
              Login
            </router-link>
            <router-link to="/demo" class="nav-demo">
              Request a Demo
              <SiteIcon name="arrowRight" :size="16" />
            </router-link>
          </div>

          <button class="nav-burger" type="button" aria-label="Open menu" @click="drawer = true">
            <SiteIcon name="menu" :size="22" />
          </button>
        </div>
      </div>
    </header>

    <!-- The drawer is a sibling of <header>, not inside it. The header's
         backdrop-filter makes it the containing block for position:fixed
         children, which would clip the drawer to the header's height. -->
    <transition name="drawer">
      <div v-if="drawer" class="drawer-overlay" @click="drawer = false">
        <div class="drawer-panel" role="dialog" aria-label="Menu" @click.stop>
          <div class="drawer-header">
            <BrandMark :size="40" />
            <span class="nav-wordmark">
              <span class="wm-top">{{ brand.top }}</span>
              <span class="wm-bottom">Collectionz</span>
            </span>
            <button class="drawer-close" type="button" aria-label="Close menu" @click="drawer = false">
              <SiteIcon name="close" :size="20" />
            </button>
          </div>

          <nav class="drawer-nav" aria-label="Main">
            <a
              v-for="link in links"
              :key="link.to"
              class="drawer-link"
              :class="{ 'is-active': isActive(link) }"
              @click="navigateTo(link.to)"
            >
              <SiteIcon :name="link.icon" :size="20" />
              <span>{{ link.label }}</span>
            </a>
          </nav>

          <div class="drawer-actions">
            <a class="drawer-btn drawer-btn-outline" @click="navigateTo('/login')">
              <SiteIcon name="login" :size="18" /> Login
            </a>
            <a class="drawer-btn drawer-btn-primary" @click="navigateTo('/demo')">
              <SiteIcon name="calendarCheck" :size="18" /> Request a Demo
            </a>
          </div>

          <div class="drawer-contact">
            <a :href="contact.phoneHref"><SiteIcon name="phone" :size="16" />{{ contact.phoneLabel }}</a>
            <a :href="'mailto:' + contact.email"><SiteIcon name="mail" :size="16" />{{ contact.email }}</a>
          </div>
        </div>
      </div>
    </transition>
  </div>
</template>

<script>
import BrandMark from "@/components/BrandMark.vue";
import SiteIcon from "@/components/SiteIcon.vue";
import { BRAND, CONTACT, NAV_LINKS } from "@/config/site";

export default {
  name: "AppNavigation",
  components: { BrandMark, SiteIcon },
  data() {
    return {
      drawer: false,
      isScrolled: false,
      brand: BRAND,
      contact: CONTACT,
      links: NAV_LINKS,
    };
  },
  watch: {
    drawer(open) {
      document.body.style.overflow = open ? "hidden" : "";
    },
  },
  mounted() {
    window.addEventListener("scroll", this.handleScroll, { passive: true });
    window.addEventListener("keydown", this.handleKey);
    this.handleScroll();
  },
  beforeDestroy() {
    window.removeEventListener("scroll", this.handleScroll);
    window.removeEventListener("keydown", this.handleKey);
    document.body.style.overflow = "";
  },
  methods: {
    handleScroll() {
      this.isScrolled = window.scrollY > 12;
    },
    handleKey(e) {
      if (e.key === "Escape") this.drawer = false;
    },
    isActive(link) {
      const path = this.$route.path;
      return link.exact ? path === link.to : path === link.to || path.startsWith(link.to + "/");
    },
    navigateTo(route) {
      this.drawer = false;
      if (this.$route.path !== route) this.$router.push(route);
    },
  },
};
</script>

<style scoped>
@import url("https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,600;9..144,700&family=Public+Sans:wght@400;500;600;700&display=swap");

.app-nav-root {
  display: contents;
}

.app-nav {
  --ink: #0e2236;
  --ink-2: #33475b;
  --green: #1e6b52;
  --green-2: #185a45;
  --paper: #f6f1e6;
  --rule: rgba(14, 34, 54, 0.12);

  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  z-index: 999;
  background: rgba(246, 241, 230, 0.86);
  backdrop-filter: saturate(1.4) blur(12px);
  -webkit-backdrop-filter: saturate(1.4) blur(12px);
  border-bottom: 1px solid transparent;
  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  transition: background 0.25s ease, border-color 0.25s ease, box-shadow 0.25s ease;
}

.app-nav.nav-scrolled {
  background: rgba(246, 241, 230, 0.96);
  border-bottom-color: var(--rule);
  box-shadow: 0 6px 24px rgba(14, 34, 54, 0.06);
}

.nav-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;
  padding: 0 24px;
}

.nav-content {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 20px;
  height: 72px;
}

.nav-brand {
  display: flex;
  align-items: center;
  gap: 11px;
  text-decoration: none;
  color: var(--ink);
}

.nav-wordmark {
  display: flex;
  flex-direction: column;
  line-height: 1;
}

.wm-top {
  font-family: "Fraunces", Georgia, serif;
  font-size: 1.28rem;
  font-weight: 700;
  letter-spacing: -0.01em;
  color: var(--ink);
}

.wm-bottom {
  margin-top: 3px;
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.06em;
  color: var(--green);
}

.nav-links {
  display: flex;
  align-items: center;
  gap: 4px;
}

.nav-link {
  position: relative;
  padding: 10px 14px;
  font-size: 14.5px;
  font-weight: 600;
  color: var(--ink-2);
  text-decoration: none;
  white-space: nowrap;
  transition: color 0.18s ease;
}

.nav-link::after {
  content: "";
  position: absolute;
  left: 14px;
  right: 14px;
  bottom: 2px;
  height: 2px;
  border-radius: 2px;
  background: var(--green);
  transform: scaleX(0);
  transform-origin: left;
  transition: transform 0.22s ease;
}

.nav-link:hover {
  color: var(--ink);
}

.nav-link:hover::after,
.nav-link.is-active::after {
  transform: scaleX(1);
}

.nav-link.is-active {
  color: var(--ink);
}

.nav-actions {
  display: flex;
  align-items: center;
  gap: 8px;
}

.nav-login,
.nav-demo {
  display: inline-flex;
  align-items: center;
  gap: 8px;
  height: 42px;
  padding: 0 16px;
  border-radius: 6px;
  font-size: 14.5px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  transition: background 0.18s ease, color 0.18s ease, border-color 0.18s ease;
}

.nav-login {
  color: var(--ink);
  border: 1.5px solid rgba(14, 34, 54, 0.22);
}

.nav-login:hover {
  border-color: var(--ink);
}

.nav-demo {
  background: var(--green);
  color: #fff;
  border: 1.5px solid var(--green);
}

.nav-demo:hover {
  background: var(--green-2);
  border-color: var(--green-2);
}

.nav-burger {
  display: none;
  width: 44px;
  height: 44px;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(14, 34, 54, 0.18);
  border-radius: 8px;
  background: transparent;
  color: var(--ink);
  cursor: pointer;
}

/* ==================== MOBILE DRAWER ==================== */
.drawer-overlay {
  position: fixed;
  inset: 0;
  z-index: 1000;
  background: rgba(11, 29, 46, 0.45);
  backdrop-filter: blur(4px);
  -webkit-backdrop-filter: blur(4px);
}

.drawer-panel {
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  width: 340px;
  max-width: 88vw;
  display: flex;
  flex-direction: column;
  overflow-y: auto;
  padding: 20px 20px 24px;
  background: #f6f1e6;
  border-left: 1px solid rgba(14, 34, 54, 0.12);
  box-shadow: -20px 0 60px rgba(11, 29, 46, 0.25);
  font-family: "Public Sans", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  color: #0e2236;
}

.drawer-enter-active,
.drawer-leave-active {
  transition: opacity 0.25s ease;
}
.drawer-enter-active .drawer-panel,
.drawer-leave-active .drawer-panel {
  transition: transform 0.28s cubic-bezier(0.2, 0.8, 0.2, 1);
}
.drawer-enter,
.drawer-leave-to {
  opacity: 0;
}
.drawer-enter .drawer-panel,
.drawer-leave-to .drawer-panel {
  transform: translateX(100%);
}

.drawer-header {
  display: flex;
  align-items: center;
  gap: 11px;
  padding-bottom: 20px;
  margin-bottom: 8px;
  border-bottom: 1px solid rgba(14, 34, 54, 0.12);
}

.drawer-header .nav-wordmark {
  flex: 1;
}

.drawer-close {
  width: 40px;
  height: 40px;
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1.5px solid rgba(14, 34, 54, 0.18);
  border-radius: 8px;
  background: transparent;
  color: #0e2236;
  cursor: pointer;
}

.drawer-nav {
  display: flex;
  flex-direction: column;
  padding: 8px 0;
}

.drawer-link {
  display: flex;
  align-items: center;
  gap: 14px;
  padding: 15px 4px;
  border-bottom: 1px solid rgba(14, 34, 54, 0.08);
  font-size: 17px;
  font-weight: 600;
  color: #33475b;
  cursor: pointer;
  transition: color 0.18s ease, padding 0.18s ease;
}

.drawer-link .site-icon {
  color: #66727f;
}

.drawer-link:hover,
.drawer-link.is-active {
  color: #0e2236;
  padding-left: 10px;
}

.drawer-link.is-active .site-icon,
.drawer-link:hover .site-icon {
  color: #1e6b52;
}

.drawer-actions {
  display: grid;
  gap: 10px;
  margin-top: 24px;
}

.drawer-btn {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  height: 50px;
  border-radius: 6px;
  font-size: 15.5px;
  font-weight: 600;
  cursor: pointer;
}

.drawer-btn-outline {
  border: 1.5px solid rgba(14, 34, 54, 0.22);
  color: #0e2236;
}

.drawer-btn-primary {
  background: #1e6b52;
  color: #fff;
}

.drawer-contact {
  margin-top: auto;
  padding-top: 28px;
  display: grid;
  gap: 10px;
}

.drawer-contact a {
  display: flex;
  align-items: center;
  gap: 10px;
  font-size: 14px;
  color: #66727f;
  text-decoration: none;
  word-break: break-all;
}

@media (max-width: 1120px) {
  .nav-link {
    padding: 10px 10px;
  }
  .nav-link::after {
    left: 10px;
    right: 10px;
  }
}

@media (max-width: 980px) {
  .nav-links,
  .nav-actions {
    display: none;
  }
  .nav-burger {
    display: flex;
  }
}

@media (max-width: 600px) {
  .nav-container {
    padding: 0 16px;
  }
  .nav-content {
    height: 64px;
  }
}
</style>